import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProviderBookings } from '../services/bookingService';
import { FaWallet, FaChartLine, FaArrowUp, FaArrowDown, FaCalendarAlt, FaFileInvoiceDollar, FaSpinner, FaInbox } from 'react-icons/fa';
import './EarningsPage.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const EarningsPage = () => {
    const { currentUser } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('6m'); // 1m, 3m, 6m, 1y, all

    useEffect(() => {
        if (!currentUser) return;
        const fetchData = async () => {
            try {
                const data = await getProviderBookings(currentUser.uid);
                setBookings(data);
            } catch (err) {
                console.error('Failed to load earnings:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentUser]);

    // Filter bookings that have a payment / are completed
    const completedBookings = useMemo(() =>
        bookings.filter(b => ['work-completed', 'payment-done', 'completed'].includes(b.status)),
        [bookings]
    );

    // Time-filtered bookings
    const filteredBookings = useMemo(() => {
        if (timeRange === 'all') return completedBookings;
        const now = new Date();
        const months = { '1m': 1, '3m': 3, '6m': 6, '1y': 12 }[timeRange] || 6;
        const cutoff = new Date(now.getFullYear(), now.getMonth() - months, 1);
        return completedBookings.filter(b => new Date(b.createdAt) >= cutoff);
    }, [completedBookings, timeRange]);

    // Compute stats
    const stats = useMemo(() => {
        const totalEarnings = filteredBookings.reduce((sum, b) => sum + (b.finalPrice || b.price || 0), 0);
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        const thisMonthEarnings = completedBookings
            .filter(b => {
                const d = new Date(b.createdAt);
                return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
            })
            .reduce((sum, b) => sum + (b.finalPrice || b.price || 0), 0);
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
        const lastMonthEarnings = completedBookings
            .filter(b => {
                const d = new Date(b.createdAt);
                return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
            })
            .reduce((sum, b) => sum + (b.finalPrice || b.price || 0), 0);
        const growth = lastMonthEarnings > 0
            ? ((thisMonthEarnings - lastMonthEarnings) / lastMonthEarnings * 100).toFixed(1)
            : thisMonthEarnings > 0 ? 100 : 0;
        return { totalEarnings, thisMonthEarnings, lastMonthEarnings, growth: Number(growth), jobCount: filteredBookings.length };
    }, [filteredBookings, completedBookings]);

    // Monthly chart data
    const monthlyData = useMemo(() => {
        const months = { '1m': 1, '3m': 3, '6m': 6, '1y': 12, 'all': 12 }[timeRange] || 6;
        const now = new Date();
        const data = [];
        for (let i = months - 1; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const month = d.getMonth();
            const year = d.getFullYear();
            const monthBookings = completedBookings.filter(b => {
                const bd = new Date(b.createdAt);
                return bd.getMonth() === month && bd.getFullYear() === year;
            });
            const amount = monthBookings.reduce((sum, b) => sum + (b.finalPrice || b.price || 0), 0);
            data.push({ month: MONTH_NAMES[month], year, amount, jobs: monthBookings.length });
        }
        return data;
    }, [completedBookings, timeRange]);

    const maxAmount = Math.max(...monthlyData.map(d => d.amount), 1);

    // Service breakdown
    const serviceBreakdown = useMemo(() => {
        const map = {};
        filteredBookings.forEach(b => {
            const key = b.serviceCategory || b.service || 'Other';
            if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
            map[key].total += (b.finalPrice || b.price || 0);
            map[key].count += 1;
        });
        const arr = Object.values(map).sort((a, b) => b.total - a.total);
        const grandTotal = arr.reduce((s, x) => s + x.total, 0) || 1;
        return arr.map(x => ({ ...x, pct: ((x.total / grandTotal) * 100).toFixed(1) }));
    }, [filteredBookings]);

    // Transactions (all bookings for this provider, sorted newest first)
    const transactions = useMemo(() =>
        bookings
            .filter(b => b.status !== 'pending' && b.status !== 'rejected')
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 20),
        [bookings]
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);
    };

    if (loading) {
        return (
            <div className="earnings-page">
                <div className="earnings-loading">
                    <FaSpinner className="spin" />
                    <p>Loading your earnings...</p>
                </div>
            </div>
        );
    }

    // SVG chart dimensions
    const chartW = 700, chartH = 280, padL = 60, padR = 20, padT = 30, padB = 50;
    const plotW = chartW - padL - padR;
    const plotH = chartH - padT - padB;
    const barGap = 12;
    const barW = Math.min(48, (plotW / monthlyData.length) - barGap);

    return (
        <div className="earnings-page">
            <div className="container">
                {/* Header */}
                <div className="ep-header">
                    <div className="ep-header-text">
                        <h1>Earnings</h1>
                        <p>Your income & payment history</p>
                    </div>
                    <div className="ep-time-filters">
                        {[{ k: '1m', l: '1M' }, { k: '3m', l: '3M' }, { k: '6m', l: '6M' }, { k: '1y', l: '1Y' }, { k: 'all', l: 'All' }].map(f => (
                            <button key={f.k} className={`tf-btn ${timeRange === f.k ? 'active' : ''}`} onClick={() => setTimeRange(f.k)}>{f.l}</button>
                        ))}
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="ep-stats-grid">
                    <div className="ep-stat-card ep-stat-primary">
                        <div className="ep-stat-icon"><FaWallet /></div>
                        <div className="ep-stat-info">
                            <span className="ep-stat-label">Total Earnings</span>
                            <span className="ep-stat-value">{formatCurrency(stats.totalEarnings)}</span>
                            <span className="ep-stat-sub">{stats.jobCount} completed jobs</span>
                        </div>
                    </div>
                    <div className="ep-stat-card">
                        <div className="ep-stat-icon"><FaCalendarAlt /></div>
                        <div className="ep-stat-info">
                            <span className="ep-stat-label">This Month</span>
                            <span className="ep-stat-value">{formatCurrency(stats.thisMonthEarnings)}</span>
                        </div>
                    </div>
                    <div className="ep-stat-card">
                        <div className="ep-stat-icon"><FaChartLine /></div>
                        <div className="ep-stat-info">
                            <span className="ep-stat-label">Growth</span>
                            <span className={`ep-stat-value ${stats.growth >= 0 ? 'positive' : 'negative'}`}>
                                {stats.growth >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(stats.growth)}%
                            </span>
                            <span className="ep-stat-sub">vs last month</span>
                        </div>
                    </div>
                </div>

                {/* Income Chart */}
                <div className="ep-chart-card">
                    <div className="ep-chart-header">
                        <h2>Income Overview</h2>
                        <div className="ep-chart-legend">
                            <span className="legend-dot"></span> Monthly Earnings
                        </div>
                    </div>
                    {monthlyData.every(d => d.amount === 0) ? (
                        <div className="ep-chart-empty">
                            <FaInbox />
                            <p>No earnings data for this period</p>
                        </div>
                    ) : (
                        <div className="ep-chart-wrapper">
                            <svg viewBox={`0 0 ${chartW} ${chartH}`} className="ep-chart-svg" preserveAspectRatio="xMidYMid meet">
                                {/* Grid lines */}
                                {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
                                    const y = padT + plotH - (frac * plotH);
                                    return (
                                        <g key={i}>
                                            <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#e5e5e5" strokeWidth="1" />
                                            <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#737373">
                                                {formatCurrency(maxAmount * frac)}
                                            </text>
                                        </g>
                                    );
                                })}
                                {/* Bars */}
                                {monthlyData.map((d, i) => {
                                    const x = padL + (plotW / monthlyData.length) * i + (plotW / monthlyData.length - barW) / 2;
                                    const h = (d.amount / maxAmount) * plotH;
                                    const y = padT + plotH - h;
                                    return (
                                        <g key={i} className="ep-bar-group">
                                            <defs>
                                                <linearGradient id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#FFD700" />
                                                    <stop offset="100%" stopColor="#FFC700" />
                                                </linearGradient>
                                            </defs>
                                            <rect x={x} y={y} width={barW} height={h} rx={4} fill={`url(#barGrad${i})`} className="ep-bar" />
                                            {d.amount > 0 && (
                                                <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize="11" fontWeight="600" fill="#171717">
                                                    {formatCurrency(d.amount)}
                                                </text>
                                            )}
                                            <text x={x + barW / 2} y={padT + plotH + 20} textAnchor="middle" fontSize="12" fill="#525252" fontWeight="500">
                                                {d.month}
                                            </text>
                                        </g>
                                    );
                                })}
                            </svg>
                        </div>
                    )}
                    <div className="ep-chart-summary">
                        <div className="ep-summary-item">
                            <span className="ep-summary-label">Period Total</span>
                            <span className="ep-summary-value">{formatCurrency(monthlyData.reduce((s, d) => s + d.amount, 0))}</span>
                        </div>
                        <div className="ep-summary-item">
                            <span className="ep-summary-label">Avg / Month</span>
                            <span className="ep-summary-value">
                                {formatCurrency(Math.round(monthlyData.reduce((s, d) => s + d.amount, 0) / (monthlyData.filter(d => d.amount > 0).length || 1)))}
                            </span>
                        </div>
                        <div className="ep-summary-item">
                            <span className="ep-summary-label">Best Month</span>
                            <span className="ep-summary-value">
                                {(() => { const best = monthlyData.reduce((a, b) => a.amount > b.amount ? a : b, { amount: 0 }); return best.amount > 0 ? `${best.month} — ${formatCurrency(best.amount)}` : '—'; })()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Service Breakdown */}
                {serviceBreakdown.length > 0 && (
                    <div className="ep-breakdown-card">
                        <h2>Earnings by Service</h2>
                        <div className="ep-breakdown-list">
                            {serviceBreakdown.map((svc, i) => (
                                <div key={i} className="ep-breakdown-row">
                                    <div className="ep-breakdown-info">
                                        <span className="ep-breakdown-name">{svc.name}</span>
                                        <span className="ep-breakdown-meta">{svc.count} job{svc.count !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="ep-breakdown-bar-wrap">
                                        <div className="ep-breakdown-bar" style={{ width: `${svc.pct}%` }}></div>
                                    </div>
                                    <div className="ep-breakdown-amount">
                                        <span className="ep-bd-value">{formatCurrency(svc.total)}</span>
                                        <span className="ep-bd-pct">{svc.pct}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Transactions */}
                <div className="ep-transactions-card">
                    <div className="ep-txn-header">
                        <h2><FaFileInvoiceDollar /> Transactions</h2>
                        <span className="ep-txn-count">{transactions.length} records</span>
                    </div>
                    {transactions.length === 0 ? (
                        <div className="ep-txn-empty">
                            <FaInbox />
                            <p>No transactions yet. Complete jobs to see them here.</p>
                        </div>
                    ) : (
                        <div className="ep-txn-list">
                            {transactions.map((txn) => (
                                <div key={txn.id} className="ep-txn-row">
                                    <div className="ep-txn-left">
                                        <div className={`ep-txn-dot ${txn.status}`}></div>
                                        <div className="ep-txn-details">
                                            <span className="ep-txn-customer">{txn.customerName || 'Customer'}</span>
                                            <span className="ep-txn-service">{txn.serviceCategory || txn.service || '—'}</span>
                                        </div>
                                    </div>
                                    <div className="ep-txn-center">
                                        <span className="ep-txn-date">{formatDate(txn.createdAt)}</span>
                                    </div>
                                    <div className="ep-txn-right">
                                        <span className="ep-txn-amount">{formatCurrency(txn.finalPrice || txn.price)}</span>
                                        <span className={`ep-txn-status ${txn.status}`}>{txn.status.replace(/-/g, ' ')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EarningsPage;
